import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { fxRate, sumarDias, usdToCordobas, type Centavos, type Settings } from '@abastece/shared';
import { ACCOUNTS, CUENTAS_EFECTIVO, balanceGeneral, estadoResultados, fiadoDeVenta, netoDeVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import type { DineroDeAnalisis } from '../lib/analisis.js';
import {
  DETALLE_AGUINALDO,
  DIAS_DE_PRONOSTICO,
  capitalDeTrabajo,
  comprasPorPagar,
  gastosPorPagar,
  deudasSinFecha,
  diasMedidos,
  pagosDePlanilla,
  pronosticoDeCaja,
  type Concepto,
  type Salida,
} from '../lib/flujo.js';
import { leerPendientes } from '../lib/gastosFijos.js';
import { diasDelRango, ventasDelPeriodo } from '../lib/libros.js';
import { SEMANAS_DE_RITMO, ventaUsualPorDiaSemana } from '../lib/proyeccion.js';
import { useT, type Clave } from '../i18n/index.js';
import { etiquetaCuenta } from '../i18n/dominio.js';
import { Barras, Linea } from './Graficas.js';
import { Cargando, Corta, Fila, Nada, Seccion, useFechas } from './PiezasDeAnalisis.js';

/**
 * «Dinero» in «Análisis» (the owner, 2026-10-13, of what the page could add: «let's build all of
 * it»): the money there is now and where, the next thirty days and their lowest day, and the money
 * it takes to run the shop. About now, so the periods are not asked. The arithmetic is
 * `lib/flujo.ts`.
 */

/** The days the shop's habits are read over: what a weekday sells and how it is paid, eight weeks. */
const DIAS_DE_HABITO = 7 * SEMANAS_DE_RITMO;
/** The days what turns into money is measured over: three months. */
const DIAS_DE_GIRO = 90;

const CONCEPTO: Record<Concepto, Clave> = {
  ventas: 'analisis.dinero.ventas',
  cobros: 'analisis.dinero.cobros',
  reponer: 'analisis.dinero.reponer',
  planilla: 'analisis.dinero.planilla',
  fijos: 'analisis.dinero.fijos',
  compras: 'analisis.dinero.compras',
  gastos: 'analisis.dinero.gastos',
  deudas: 'analisis.dinero.deudas',
};

const nombreDeCuenta = (codigo: string, config: Settings): string => {
  const a = Object.values(ACCOUNTS).find((x) => x.code === codigo);
  return etiquetaCuenta(codigo, a?.name ?? codigo, config.cuentasBanco);
};

/**
 * What «Dinero» reads and works out: the money there is, the next thirty days, what falls due and
 * the working capital. «Hoy» reads the lowest day and the books from it too. Undefined while it is
 * read.
 */
export function useDinero(hoyEs: string) {
  const datos = useLiveQuery(async () => {
    const fin = sumarDias(hoyEs, DIAS_DE_PRONOSTICO - 1);
    const desdeGiro = sumarDias(hoyEs, -(DIAS_DE_GIRO - 1));
    const [libro, primero, config, ventas, abonos, compras, gastos, empleados, corridas, proveedores, fijos] = await Promise.all([
      // The balance is everything the books ever wrote, to today.
      diasDelRango({ desde: '2000-01-01', hasta: hoyEs }),
      db.ventas.orderBy('fecha').first(),
      readSettings(),
      ventasDelPeriodo({ desde: desdeGiro, hasta: hoyEs }),
      db.abonos.where('fecha').between(sumarDias(hoyEs, -(DIAS_DE_HABITO - 1)), hoyEs, true, true).toArray(),
      db.compras.toArray(),
      db.gastos.filter((g) => g.formaPago === 'credito' && !g.anulado).toArray(),
      db.empleados.toArray(),
      db.planillas.toArray(),
      db.proveedores.toArray(),
      leerPendientes(hoyEs, DIAS_DE_PRONOSTICO - 1),
    ]);
    return { libro, primeraVenta: primero?.fecha ?? null, config, ventas, abonos, compras, gastos, empleados, corridas, proveedores, fijos, fin, desdeGiro };
  }, [hoyEs]);

  const calculo = useMemo(() => {
    if (!datos) return null;
    const { config } = datos;
    const balance = balanceGeneral(datos.libro, hoyEs);
    const enCaja = balance.activos.filter((l) => CUENTAS_EFECTIVO.includes(l.codigo) && l.monto !== 0);
    const saldo = enCaja.reduce((a, l) => a + l.monto, 0);

    // The shop's habits the last eight weeks, from its first sale on.
    const desdeHabito = sumarDias(hoyEs, -(DIAS_DE_HABITO - 1));
    const recientes = datos.ventas.filter((v) => !v.anulada && v.fecha >= desdeHabito);
    let total = 0;
    let fiado = 0;
    let ingreso = 0;
    let costo = 0;
    for (const v of recientes) {
      const neto = netoDeVenta(v);
      total += v.total;
      fiado += fiadoDeVenta(v);
      ingreso += neto.ingreso;
      costo += neto.costo;
    }
    const vendidoHoy = recientes.filter((v) => v.fecha === hoyEs).reduce((a, v) => a + netoDeVenta(v).ingreso, 0);
    const desdeCobros = datos.primeraVenta && datos.primeraVenta > desdeHabito ? datos.primeraVenta : desdeHabito;
    const cobros = datos.abonos.filter((a) => !a.anulado).reduce((x, a) => x + a.monto, 0);

    // What falls due on a day of its own.
    const planilla = pagosDePlanilla({ empleados: datos.empleados, corridas: datos.corridas, rates: config, hoy: hoyEs, hasta: datos.fin });
    const sinMonto = datos.fijos.filter((o) => o.fijo.monto === null).map((o) => o.fijo.descripcion);
    const fijos: Salida[] = datos.fijos
      .filter((o) => o.fijo.monto !== null)
      .map((o) => ({
        fecha: o.vence < hoyEs ? hoyEs : o.vence,
        concepto: 'fijos',
        monto: o.fijo.moneda === 'USD' ? usdToCordobas(o.fijo.monto! as Centavos, fxRate(config.fxTasaOficial)) : o.fijo.monto!,
        detalle: o.fijo.descripcion,
      }));
    const salidas = [
      ...planilla.salidas,
      ...fijos,
      ...comprasPorPagar(datos.compras, hoyEs, datos.fin),
      ...gastosPorPagar(datos.gastos, hoyEs),
      ...deudasSinFecha(balance.pasivos, hoyEs),
    ].sort(
      (a, b) => a.fecha.localeCompare(b.fecha) || b.monto - a.monto,
    );
    const pronostico = pronosticoDeCaja({
      hoy: hoyEs,
      saldoInicial: saldo,
      ventaPorDiaSemana: ventaUsualPorDiaSemana(datos.ventas, hoyEs),
      vendidoHoy,
      contadoBp: total > 0 ? Math.round(((total - fiado) * 10_000) / total) : 10_000,
      margenBp: ingreso > 0 ? Math.round(((ingreso - costo) * 10_000) / ingreso) : 0,
      cobrosPorDia: cobros / diasMedidos(desdeCobros, hoyEs),
      salidas,
    });

    // The money it takes to run the shop, and how long it stays out.
    const desdeGiro = datos.primeraVenta && datos.primeraVenta > datos.desdeGiro ? datos.primeraVenta : datos.desdeGiro;
    const dias = diasMedidos(desdeGiro, hoyEs);
    const resultado = estadoResultados(datos.libro, { desde: desdeGiro, hasta: hoyEs });
    const capital = capitalDeTrabajo({
      activos: balance.activos,
      pasivos: balance.pasivos,
      costoDeVentas: resultado.costos.find((l) => l.codigo === ACCOUNTS.COSTO_VENTAS.code)?.monto ?? 0,
      fiadoDado: datos.ventas.filter((v) => !v.anulada && v.fecha >= desdeGiro).reduce((a, v) => a + fiadoDeVenta(v), 0),
      comprasACredito: datos.compras.filter((c) => !c.anulada && c.formaPago === 'credito' && c.fecha >= desdeGiro && c.fecha <= hoyEs).reduce((a, c) => a + c.totalCordobas, 0),
      dias,
    });
    return { enCaja, saldo, pronostico, salidas, porDia: planilla.porDia, sinMonto, capital, dias };
  }, [datos, hoyEs]);
  return datos && calculo ? { datos, ...calculo } : undefined;
}

export function VistaDinero({ hoyEs, dinero }: { hoyEs: string; dinero: DineroDeAnalisis }) {
  const t = useT();
  const f = useFechas();
  const leido = useDinero(hoyEs);
  if (!leido) return <Cargando />;
  const { datos, enCaja, saldo, pronostico, salidas, porDia, sinMonto, capital, dias } = leido;
  const proveedor = (id: string) => datos.proveedores.find((p) => p.id === id)?.nombre ?? '—';
  const dichoDe = (s: Salida): string => {
    switch (s.concepto) {
      case 'planilla': {
        if (s.detalle === DETALLE_AGUINALDO) return t('analisis.dinero.aguinaldo');
        const [desde, hasta] = s.detalle.split('|') as [string, string];
        return t('analisis.dinero.planillaDe', { desde: f.corta(desde), hasta: f.corta(hasta) });
      }
      case 'compras':
        return proveedor(s.detalle);
      case 'deudas':
        return nombreDeCuenta(s.detalle, datos.config);
      default:
        return s.detalle;
    }
  };
  const bajo = pronostico.minimo.saldo < 0;
  const conceptos = (Object.keys(CONCEPTO) as Concepto[]).filter((c) => Math.round(pronostico.porConcepto[c]) !== 0);

  return (
    <div className="flex flex-col gap-4" data-analisis="dinero">
      <Seccion titulo={t('analisis.dinero.hoyTitulo')} ayuda={t('analisis.dinero.hoyAyuda')} datos="dinero-hoy">
        <p className={`mb-3 text-sm ${saldo < 0 ? 'text-rose-400' : 'text-slate-300'}`} data-dinero-total>
          {saldo < 0 ? t('analisis.dinero.bajoCero', { monto: dinero(-saldo) }) : t('analisis.dinero.hay', { monto: dinero(saldo) })}
        </p>
        {enCaja.length === 0 ? (
          <Nada texto={t('analisis.dinero.nada')} />
        ) : (
          <Barras filas={enCaja.map((l) => ({ clave: l.codigo, etiqueta: nombreDeCuenta(l.codigo, datos.config), valor: l.monto, texto: dinero(l.monto) }))} datos="dinero" />
        )}
      </Seccion>

      <Seccion titulo={t('analisis.dinero.pronosticoTitulo', { n: DIAS_DE_PRONOSTICO })} ayuda={t('analisis.dinero.pronosticoAyuda', { n: SEMANAS_DE_RITMO })} datos="pronostico">
        <div className="flex flex-col gap-1 text-sm text-slate-300">
          <p data-pronostico-final>
            {pronostico.final < 0
              ? t('analisis.dinero.finalFalta', { n: DIAS_DE_PRONOSTICO, monto: dinero(Math.round(-pronostico.final)) })
              : t('analisis.dinero.final', { n: DIAS_DE_PRONOSTICO, monto: dinero(Math.round(pronostico.final)) })}
          </p>
          <p className={`font-medium ${bajo ? 'text-rose-400' : 'text-emerald-300'}`} data-pronostico-minimo={bajo ? 'falta' : 'alcanza'}>
            {bajo
              ? t('analisis.dinero.falta', { fecha: f.conDia(pronostico.minimo.fecha), monto: dinero(Math.round(-pronostico.minimo.saldo)) })
              : t('analisis.dinero.minimo', { fecha: f.conDia(pronostico.minimo.fecha), monto: dinero(Math.round(pronostico.minimo.saldo)) })}
          </p>
        </div>
        <div className="mt-4">
          <Linea
            puntos={pronostico.dias.map((d) => ({
              clave: d.fecha,
              etiqueta: f.corta(d.fecha),
              valor: Math.round(d.saldo),
              texto: t('analisis.dinero.columna', { fecha: f.conDia(d.fecha), monto: dinero(Math.round(d.saldo)) }),
            }))}
            resumen={t('analisis.dinero.pronosticoTitulo', { n: DIAS_DE_PRONOSTICO })}
          />
        </div>
        <div className="mt-4">
          <Barras
            filas={conceptos.map((c) => {
              const monto = Math.round(pronostico.porConcepto[c]);
              return { clave: c, etiqueta: t(CONCEPTO[c]), valor: Math.abs(monto), texto: dinero(monto), tono: monto < 0 ? 'bg-rose-500/70' : 'bg-emerald-500' };
            })}
            datos="conceptos"
          />
        </div>
        <ul className="mt-4 flex flex-col gap-1 text-xs text-slate-500">
          <li>{t('analisis.dinero.supuestos')}</li>
          {porDia > 0 && <li data-pronostico-por-dia>{t('analisis.dinero.porDia', { n: porDia })}</li>}
          {sinMonto.length > 0 && <li data-pronostico-sin-monto>{t('analisis.dinero.sinMonto', { lista: sinMonto.join(', ') })}</li>}
        </ul>
      </Seccion>

      <Seccion titulo={t('analisis.dinero.venceTitulo', { n: DIAS_DE_PRONOSTICO })} ayuda={t('analisis.dinero.venceAyuda')} datos="vence">
        {salidas.length === 0 ? (
          <Nada texto={t('analisis.dinero.venceNada')} />
        ) : (
          <Corta filas={salidas}>
            {(vistas) => (
              <ul className="-my-2.5 divide-y divide-slate-800">
                {vistas.map((s, i) => (
                  <Fila
                    key={`${s.concepto}-${s.detalle}-${s.fecha}-${i}`}
                    nombre={`${s.concepto}-${s.fecha}`}
                    a={<span>{dichoDe(s)}</span>}
                    detalle={`${t(CONCEPTO[s.concepto])} · ${s.fecha === hoyEs ? t('analisis.dinero.hoyMismo') : f.conDia(s.fecha)}`}
                    valor={dinero(s.monto)}
                  />
                ))}
              </ul>
            )}
          </Corta>
        )}
      </Seccion>

      <Seccion titulo={t('analisis.capital.titulo')} ayuda={t('analisis.capital.ayuda', { n: dias })} datos="capital">
        <div className="flex flex-col gap-2 text-sm text-slate-300">
          <p data-capital>
            {t('analisis.capital.tiene', {
              capital: dinero(capital.capital),
              dinero: dinero(capital.dinero),
              inventario: dinero(capital.inventario),
              fiado: dinero(capital.fiado),
              otros: dinero(capital.otros),
              proveedores: dinero(capital.proveedores),
              deudas: dinero(capital.otrasDeudas),
            })}
          </p>
          {capital.ciclo !== null && (
            <p className="font-medium" data-capital-ciclo>
              {t(capital.ciclo < 0 ? 'analisis.capital.cicloAntes' : 'analisis.capital.ciclo', {
                n: Math.abs(capital.ciclo),
                inventario: capital.diasInventario ?? 0,
                cobro: capital.diasCobro ?? 0,
                pago: capital.diasPago ?? 0,
              })}
            </p>
          )}
        </div>
      </Seccion>
    </div>
  );
}
